import {
  DivColNode,
  DivRowNode,
  HeaderSchemaVersion,
  ImageNode,
  MenuPostion,
  NodeType,
  PageNode,
} from "../../../../types/PageNode";
import { DESKTOP_CONFIG, HEADER_STYLE_DEFAULT, MOBILE_PAGE_CONFIG, MENU_ICON_SET_STYLE } from "../../../../data/constants";
import { getNewNode, getNodeWithNewIds } from "../../../NodeService";
import { OnClickActionType } from "../../../../types/OnClickAction";

interface SubpageForNav {
  id?: number | null;
  title?: string | null;
  publicUrl?: string | null;
}

export default class BasicHeaderNodeFactory {
  public static getHeaderNode = (
    parentNode: PageNode,
    navigationPages: SubpageForNav[] = [],
    isMobilePage = false,
  ): PageNode => {
    const styles = isMobilePage ? MOBILE_PAGE_CONFIG : DESKTOP_CONFIG;
    const header = getNewNode(NodeType.HeaderNode, parentNode, {
      tagName: "header",
      className: "basic-header navbar",
      schemaVersion: HeaderSchemaVersion.Basic,
      isSticky: false,
      burgerBehavior: MenuPostion.Right,
      iconsSet: MENU_ICON_SET_STYLE.LIGHT,
      customStyle: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        paddingTop: styles.headerPaddingY,
        paddingRight: styles.headerPaddingX,
        paddingBottom: styles.headerPaddingY,
        paddingLeft: styles.headerPaddingX,
        background: HEADER_STYLE_DEFAULT.BACKGROUND,
      },
      customStyleMobile: styles.isMobileSupportNeeded ? {
        paddingTop: HEADER_STYLE_DEFAULT.PADDING_MOBILE_Y,
        paddingRight: HEADER_STYLE_DEFAULT.PADDING_MOBILE_X,
        paddingBottom: HEADER_STYLE_DEFAULT.PADDING_MOBILE_Y,
        paddingLeft: HEADER_STYLE_DEFAULT.PADDING_MOBILE_X,
      } : {},
      isMixedPadding: true,
      isMobileMixedPadding: styles.isMobileSupportNeeded,
      isMobileStyleContainerSize: styles.isMobileSupportNeeded,
    });

    const row = getNewNode<DivRowNode>(NodeType.DivRowNode, header, {
      tagName: "div",
      className: "row",
      customStyle: {
        alignItems: "center",
        width: "100%",
        marginTop: "0",
        marginLeft: "0",
        marginRight: "0",
        marginBottom: "0",
      },
      structure: 2,
      mobileStructure: 2,
      breadCrumbTitle: "Header",
    });

    const brandColumn = getNewNode<DivColNode>(NodeType.DivColNode, row, {
      tagName: "div",
      className: "block d-flex align-items-center",
      colSize: 6,
      colSizeMobile: 6,
      breadCrumbTitle: "Brand",
      customStyle: {
        paddingLeft: "0",
        paddingRight: "0",
      },
    });
    const logo = getNewNode<ImageNode>(NodeType.ImageNode, brandColumn, {
      tagName: "img",
      className: "img-fluid d-block",
      src: "https://cdn3.me-qr.com/constructor/user/99901/1770295422.png",
      alt: "Company logo",
      loading: "eager",
      customStyle: { width: styles.logoSize, height: styles.logoSize, marginRight: styles.logoMargin },
      customStyleMobile: styles.isMobileSupportNeeded
        ? { width: MOBILE_PAGE_CONFIG.logoSize, height: MOBILE_PAGE_CONFIG.logoSize, marginRight: MOBILE_PAGE_CONFIG.logoMargin }
        : {},
      isMobileStyleContainerSize: styles.isMobileSupportNeeded,
      disallowedAddElements: true,
    });
    const companyName = getNewNode(NodeType.TextNode, brandColumn, {
      tagName: "text",
      value: '<span class="basic-header-company-name"><strong>Company</strong></span>',
      textClasses: { "basic-header-company-name": styles.companyText },
      textMobileClasses: styles.isMobileSupportNeeded
        ? { "basic-header-company-name": MOBILE_PAGE_CONFIG.companyText }
        : {},
      isMobileTextEnabled: styles.isMobileSupportNeeded,
      disallowedAddElements: true,
    });
    brandColumn.nodes.push(logo, companyName);

    const navigationColumn = getNewNode<DivColNode>(NodeType.DivColNode, row, {
      tagName: "div",
      className: "block d-flex",
      backClassesIndexed: {
        horizontalAlignment: "justify-content-end",
        verticalAlignment: "align-items-center",
      },
      colSize: 6,
      colSizeMobile: 6,
      breadCrumbTitle: "Navigation",
      customStyle: {
        paddingLeft: "0",
        paddingRight: "0",
      },
    });
    const navigation = getNewNode(NodeType.NavigationRegionsNode, navigationColumn, {
      tagName: "nav",
      className: "navigation-regions",
      breadCrumbTitle: "Navigation",
      disallowedAddElements: true,
    });

    const seenPageIds = new Set<number>();
    const navItems = navigationPages
      .filter((sp): sp is { id: number; title: string; publicUrl: string } => {
        if (!sp.id || !sp.title || !sp.publicUrl || seenPageIds.has(sp.id)) {
          return false;
        }
        seenPageIds.add(sp.id);
        return true;
      })
      .map((sp) =>
        getNewNode(NodeType.ButtonNode, navigation, {
          tagName: "a",
          className: "btn",
          value: `<div><span class="custom-nav-item-text">${sp.title}</span></div>`,
          textClasses: { "custom-nav-item-text": "color: #3A83F6;" },
          customStyle: {
            ...(isMobilePage && { width: "100%" }),
            paddingTop: "8px",
            paddingBottom: "8px",
            paddingLeft: "16px",
            paddingRight: "16px",
            hoverColor: "#1E63D3",
          },
          customStyleMobile: isMobilePage ? {} : { width: "100%" },
          isMobileStyleContainerSize: !isMobilePage,
          isMixedPadding: true,
          onClickAction: {
            type: OnClickActionType.OpenPage,
            value: sp.publicUrl,
          },
        }),
      );

    navigation.nodes.push(...navItems);

    navigationColumn.nodes.push(navigation);
    row.nodes.push(brandColumn, navigationColumn);
    header.nodes.push(row);

    return getNodeWithNewIds(header);
  };
}
